'use client';
import { forwardRef, useImperativeHandle } from 'react';

import { useTranslation } from '@lichtblick/i18next';
import { TextVariants } from '@lichtblick/theme';
import { GetAppointment_V1Query, AppointmentReducerState } from '@lichtblick/types';

import { Container } from './AppointmentDrawer.styles';
import { AppointmentDrawerForm } from './AppointmentDrawerForm';
import { type ItemEvent, useAppointmentDrawer } from './useAppointmentDrawer';

import { Drawer, Snackbar, Text } from '..';

export const AppointmentDrawer = forwardRef<
  { resetForm: () => void },
  {
    appointment?: GetAppointment_V1Query['getAppointment_V1']['data'];
    initialFormState?: AppointmentReducerState;
    isLoadingAppointment?: boolean;
    isOpen: boolean;
    notFoundMessage?: string;
    onClose: () => void;
    onDelete?: (id: number) => void;
    onSave?: (itemEvent?: ItemEvent) => void;
    trigger?: React.ReactElement;
  }
>(
  (
    {
      appointment,
      initialFormState,
      isLoadingAppointment,
      isOpen,
      notFoundMessage,
      onClose,
      onDelete,
      onSave,
      trigger,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const hookResult = useAppointmentDrawer({ appointment, initialFormState, onClose, onDelete, onSave });

    useImperativeHandle(ref, () => ({ resetForm: hookResult.resetForm }));
    const isLoading = isLoadingAppointment || hookResult.isLoading;
    const isErrorState = !isLoading && !!notFoundMessage;

    return (
      <Drawer
        cancelLabel={isErrorState ? undefined : t('common:ui.cancel')}
        deleteText={t('pm:deleteAppointmentConfirmation')}
        isActionContainer={!isErrorState}
        isLoading={isLoading}
        isOpen={isOpen}
        manualAcceptLabel={isErrorState ? undefined : onSave ? t('common:ui.save') : undefined}
        onAccept={() => {
          hookResult.onAcceptHandler();
        }}
        onClose={hookResult.handleDrawerClose}
        onDelete={appointment?.appointment?.id ? () => hookResult.onDeleteHandler() : undefined}
        showLoadingOverlay={hookResult.isSaving}
        title={isErrorState ? t('pm:error') : t('pm:createAppointmentTitle')}
        trigger={trigger}
      >
        <>
          <Container>
            {!isLoading && notFoundMessage ? (
              <Text $variant={TextVariants.body.l}>{notFoundMessage}</Text>
            ) : (
              !isLoading && <AppointmentDrawerForm {...hookResult} />
            )}
          </Container>
          <Snackbar
            color="black"
            isOpen={hookResult.shouldShowToast}
            message={hookResult.toastMessage}
            setOpen={() => hookResult.setShouldShowToast(false)}
            title={hookResult.toastTitle}
          />
        </>
      </Drawer>
    );
  },
);
